import { useCallback } from 'react';
import { getPrograms } from 'Queries/GraphQlQueries/Program/ProgramQueries';
import { IProgramEntityAttributes } from 'Models/Entities';
import { uniqWith } from 'lodash';
import { observer } from 'mobx-react';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import { ISelectProps, Select } from 'Views/Components/Select/Select';
import { userDiscriminatorRoleMap } from 'Models/Enums';
import usePermission from 'Hooks/usePermission';

interface IProgramSearchProps<T> extends Omit<ISelectProps<T, string>, 'options'> {
	searchByProperty: keyof IProgramEntityAttributes;
	optionValueProperty?: keyof IProgramEntityAttributes;
}

const ProgramSearch = observer(<T, >(props: IProgramSearchProps<T>) => {
	const {
		searchByProperty,
		optionValueProperty,
		...rest
	} = props;

	const mapProgramsToOptions = useCallback(
		(property: keyof IProgramEntityAttributes) => (programAttributes: IProgramEntityAttributes[]) => uniqWith(
			programAttributes,
			(p1, p2) => p1[property] === p2[property],
		)
			.map(program => ({
				display: `${program[property]}`,
				value: program[optionValueProperty || property] as string,
			}))
			.sort((a, b) => a.display.localeCompare(b.display)),
		[optionValueProperty],
	);

	const canOnlySeePublished = usePermission([
		userDiscriminatorRoleMap.UserEntity,
	]);

	const programHasFilters = canOnlySeePublished
		? [[
			{
				path: 'programVersions',
				conditions: [[{
					path: 'status',
					comparison: 'equal',
					value: ['published'],
				}]],
			},
		]] : [];

	const searchPrograms = (value: string) => {
		return getPrograms(
			value
				? [[{
					path: searchByProperty,
					comparison: 'like',
					value: [`%${value}%`],
					case: 'INVARIANT_CULTURE_IGNORE_CASE',
				}]]
				: [],
			programHasFilters,
			[],
			0,
			100,
		)
			.then(mapProgramsToOptions(searchByProperty));
	};

	const debouncedSearch = AwesomeDebouncePromise(searchPrograms, 300);

	return (
		<Select<T, string>
			initialOptions={() => debouncedSearch('')}
			options={debouncedSearch}
			{...rest}
		/>
	);
});

export default ProgramSearch;
